import {
	Component,
	computed,
	effect,
	input,
	model,
	output,
	signal,
	untracked,
	ViewEncapsulation,
} from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';
import { ContentChange } from 'ngx-quill';
import Quill from 'quill';

const QUILL_USER_SOURCE = 'user';

@Component({
	selector: 'ui-rich-text-editor',
	templateUrl: './rich-text-editor.component.html',
	styleUrl: './rich-text-editor.component.scss',
	encapsulation: ViewEncapsulation.None,
	standalone: false,
})
export class RichTextEditorComponent implements FormValueControl<string> {
	editorId = input.required<string>();
	placeholder = input<string>('');
	disabled = input<boolean>(false);
	invalid = input<boolean>(false);

	value = model<string>('');

	touch = output<void>();

	quillConfig = computed(() => {
		if (this.editorId()) {
			return {
				toolbar: '#rich-text-editor-toolbar-' + this.editorId(),
				keyboard: {
					bindings: {
						tab: {
							key: 'Tab',
							handler: () => true, // Prevent default tab behavior
						},
					},
				},
			};
		}
		return {
			toolbar: '',
		};
	});

	quillEditor: Quill | undefined;
	private currentValue: string = '';

	focused = signal(false);

	constructor() {
		effect(() => {
			const html = this.value();
			untracked(() => this.showValue(html));
		});

		effect(() => {
			const disabled = this.disabled();
			untracked(() => this.quillEditor?.enable(!disabled));
		});
	}

	/** The field writing in; Quill normalises markup, so only apply what it does not already hold. */
	private showValue(html: string): void {
		const value = html ?? '';
		if (value === this.currentValue) {
			return;
		}

		this.currentValue = value;
		this.setHtml(value);
	}

	editorCreated(editor: Quill) {
		this.quillEditor = editor;
		// Set the current value instead of always clearing to empty string
		this.setHtml(this.currentValue);
	}

	setHtml(html: string) {
		if (this.quillEditor) {
			const delta = this.quillEditor.clipboard.convert({ html });
			if (delta) {
				this.quillEditor.setContents(delta);
			}
		}
	}

	onUndo() {
		if (this.quillEditor) {
			this.quillEditor.history.undo();
		}
	}

	onRedo() {
		if (this.quillEditor) {
			this.quillEditor.history.redo();
		}
	}

	setFocus() {
		this.focused.set(true);
	}
	focusOut() {
		this.focused.set(false);
	}

	contentChanged(event: ContentChange) {
		if (event.source !== QUILL_USER_SOURCE) {
			return;
		}

		// remove all nbsp; and replace with empty string
		if (event.html) {
			event.html = event.html.toString().replace(/&nbsp;/g, ' ');
		}

		if (event.html === this.currentValue) {
			return;
		}

		this.currentValue = event.html ?? '';
		this.value.set(this.currentValue);
		this.touch.emit();
	}
}
